import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { HostStripeMessageSchema, stripeBridgeEvent } from "@froppio/host-auth";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { authService } from "@/core/auth/services/authService";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { appointmentKeys } from "@/features/appointments/hooks/appointmentQueries";
import { paymentApiService as api } from "../services/paymentApiService";
import {
  fromCashPaymentDtoToCashPayment, fromCreatePaymentRequestToCreatePaymentDto, fromPaymentDtoToPayment,
  fromRecordCashPaymentRequestToRecordCashPaymentDto, type RecordCashPaymentRequest, fromPaymentSummaryDtoToPaymentSummary,
  fromVoidPaymentKeyToVoidPaymentDto, type CreatePaymentRequest,
} from "../types/paymentTypes";

export const paymentKeys = {
  appointment: (businessId: string, appointmentId: string, userId: string) =>
    ["appointment-payments", businessId, appointmentId, userId] as const,
  detail: (businessId: string, appointmentId: string, userId: string, paymentId: string) =>
    [...paymentKeys.appointment(businessId, appointmentId, userId), paymentId] as const,
};
function usePaymentScope() {
  const { business, businessId } = useCurrentBusiness();
  const { user, getAuthGeneration } = useAuth();
  return { businessId: businessId ?? "", userId: user?.id ?? "", getAuthGeneration,
    authorized: !!businessId && !!user && business?.ownerId === user.id };
}
function usePaymentResume(authorized: boolean, refetch: () => Promise<unknown>) {
  useEffect(() => {
    if (!authorized || authService.kind !== "host") return;
    const resume = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;
      const parsed = HostStripeMessageSchema.safeParse(event.detail);
      if (parsed.success && parsed.data.type === "resume") void refetch();
    };
    window.addEventListener(stripeBridgeEvent, resume);
    return () => window.removeEventListener(stripeBridgeEvent, resume);
  }, [authorized, refetch]);
}
export function usePaymentSummary(appointmentId: string) {
  const { businessId, userId, authorized } = usePaymentScope();
  const query = useQuery({
    queryKey: paymentKeys.appointment(businessId, appointmentId, userId),
    enabled: authorized && !!appointmentId, retry: false, staleTime: 0, refetchOnWindowFocus: true,
    queryFn: async ({ signal }) => fromPaymentSummaryDtoToPaymentSummary(await api.getSummary(businessId, appointmentId, signal)),
  });
  usePaymentResume(authorized && !!appointmentId, query.refetch);
  return query;
}
export function usePayment(appointmentId: string, paymentId: string) {
  const { businessId, userId, authorized } = usePaymentScope();
  const query = useQuery({
    queryKey: paymentKeys.detail(businessId, appointmentId, userId, paymentId),
    enabled: authorized && !!appointmentId && !!paymentId, retry: false, staleTime: 0, refetchOnWindowFocus: true,
    queryFn: async ({ signal }) => {
      const payment = fromPaymentDtoToPayment(await api.getById(businessId, appointmentId, paymentId, signal));
      if (payment.id !== paymentId) throw new Error("The returned payment did not match this request.");
      return payment;
    },
    refetchInterval: query => query.state.error ? false :
      query.state.data && ["preparing", "processing", "canceling"].includes(query.state.data.status) ? 5000 : false,
  });
  usePaymentResume(authorized && !!appointmentId && !!paymentId, query.refetch);
  return query;
}
export function usePaymentMutations(appointmentId: string) {
  const scope = usePaymentScope();
  const queryClient = useQueryClient();
  const options = {
    retry: false as const,
    onMutate: () => {
      if (!scope.authorized) throw new Error("Only the business owner can collect this payment.");
      return { businessId: scope.businessId, userId: scope.userId, generation: scope.getAuthGeneration() };
    },
    onSettled: async (_data: unknown, _error: Error | null, _variables: unknown,
      context: { businessId: string; userId: string; generation: number } | undefined) => {
      if (!context || context.generation !== scope.getAuthGeneration()) return;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: paymentKeys.appointment(context.businessId, appointmentId, context.userId), refetchType: "none" }),
        queryClient.invalidateQueries({ queryKey: appointmentKeys.detail(appointmentId, context.businessId, context.userId), refetchType: "none" }),
        queryClient.invalidateQueries({ queryKey: appointmentKeys.list(context.businessId, context.userId), refetchType: "none" }),
      ]);
    },
  };
  const create = useMutation({
    ...options,
    mutationFn: async (request: CreatePaymentRequest) => fromPaymentDtoToPayment(
      await api.create(scope.businessId, appointmentId, fromCreatePaymentRequestToCreatePaymentDto(request))),
  });
  const cancel = useMutation({
    ...options,
    mutationFn: async ({ paymentId, idempotencyKey }: { paymentId: string; idempotencyKey: string }) =>
      fromPaymentDtoToPayment(await api.cancel(scope.businessId, appointmentId, paymentId, idempotencyKey)),
  });
  const forceVoid = useMutation({
    ...options,
    mutationFn: async ({ paymentId, idempotencyKey }: { paymentId: string; idempotencyKey: string }) =>
      fromPaymentDtoToPayment(await api.void(scope.businessId, appointmentId, paymentId, fromVoidPaymentKeyToVoidPaymentDto(idempotencyKey))),
  });
  const recordCash = useMutation({
    ...options,
    mutationFn: async (request: RecordCashPaymentRequest) => fromCashPaymentDtoToCashPayment(
      await api.recordCash(scope.businessId, appointmentId, fromRecordCashPaymentRequestToRecordCashPaymentDto(request))),
  });
  const voidCash = useMutation({
    ...options,
    mutationFn: async (cashPaymentId: string) =>
      fromCashPaymentDtoToCashPayment(await api.voidCash(scope.businessId, appointmentId, cashPaymentId)),
    // Voiding happens on the appointment page itself, so refresh what is on screen.
    onSuccess: (_data: unknown, _id: string, context: { businessId: string; userId: string; generation: number } | undefined) => {
      if (!context || context.generation !== scope.getAuthGeneration()) return;
      void queryClient.invalidateQueries({ queryKey: paymentKeys.appointment(context.businessId, appointmentId, context.userId) });
    },
  });
  return { create, cancel, forceVoid, recordCash, voidCash };
}
